import { chain, has, isEmpty, pick, pluck, result, union, map } from 'underscore';
import { AudienceGroup as AudienceGroupOdataSchema } from '@bingads-webui/campaign-odata-schemas';
import { AudienceGroup as AudienceGroupBulkUpsertSchema } from '@bingads-webui/odata-bulk-upsert-config';
import { edmCampaign } from '@bingads-webui/edm-campaign';
import { getBulkApiErrors } from '@bingads-webui-component-legacy/api-errors-campaign';

import { initialAudienceGroupData, AudienceGroupNegativeReferenceId } from '../constants/audience-group';
import { GENERIC_ERROR_MESSAGE_KEY } from '../constants/strings';

const audienceGroupEDM = edmCampaign.types.resolve('Model.AudienceGroup');
const audienceGroupResource = new audienceGroupEDM.ResourceIdentifier();

const whitelistedModelSchemaPropertyKeys = Object.keys(AudienceGroupOdataSchema.properties);
const whitelistedBulkModelSchemaPropertyKeys = Object.keys(AudienceGroupBulkUpsertSchema.properties);

const isBulkWrappedResponse = bulkResult => has(bulkResult, 'entity') || has(bulkResult, 'errors');

export const initialAudienceGroupViewModel = {
  data: initialAudienceGroupData,
  isValid: false,
  isDirty: false,
};

export const normalizeModelForAudienceGroup = audienceGroupModel =>
  pick(audienceGroupModel, whitelistedModelSchemaPropertyKeys);

export const modelToViewModelForAudienceGroup = (audienceGroupModel) => {
  const data = pick(audienceGroupModel, whitelistedModelSchemaPropertyKeys);

  const audiences = chain(data)
    .result('Dimensions', [])
    .filter(dimension => dimension.Type === 'Audience')
    .map(dimension => result(dimension, 'Audiences'))
    .flatten()
    .compact();

  const CustomerData = audiences
    .filter(audience => audience.Type !== 'InMarket')
    .value();

  const Interests = audiences
    .filter(audience => audience.Type === 'InMarket')
    .value();

  const profilesData = chain(data)
    .result('Dimensions', [])
    .filter(dimension => dimension.Type === 'Profile')
    .map(dimension => result(dimension, 'Profiles'))
    .flatten()
    .compact();

  const Profiles = profilesData.value();

  const companyNameTargets = profilesData
    .filter(profile => profile.Type === 'CompanyName')
    .value()
    .map(item => ({
      Target: item.Id,
      Name: item.Name,
    }));

  const industry = profilesData
    .filter(profile => profile.Type === 'Industry')
    .value()
    .map(item => item.Id);

  const jobTargets = profilesData
    .filter(profile => profile.Type === 'JobFunction')
    .value()
    .map(item => item.Id);

  const AgeRanges = chain(data)
    .result('Dimensions', [])
    .filter(dimension => dimension.Type === 'Age')
    .first()
    .result('AgeRanges')
    .value();

  const GenderTypes = chain(data)
    .result('Dimensions', [])
    .filter(dimension => dimension.Type === 'Gender')
    .first()
    .result('GenderTypes')
    .value();

  const audienceGroupViewModel = {
    data: {
      ...data,
      CustomerData,
      Interests,
      AgeRanges,
      GenderTypes,
      Profiles,
      companyNameTargets,
      industry,
      jobTargets,
    },
    isValid: true,
    isDirty: false,
  };

  return audienceGroupViewModel;
};

export const viewModelToModelForAudienceGroup = (audienceGroupViewModel) => {
  const data = pick(audienceGroupViewModel.data, whitelistedModelSchemaPropertyKeys);

  const {
    CustomerData,
    Interests,
    AgeRanges,
    GenderTypes,
    companyNameTargets = {},
    industry = {},
    jobTargets = {},
  } = audienceGroupViewModel.data;

  const dimensions = [];
  const audiences = [...CustomerData, ...Interests];

  const profiles = union(companyNameTargets || [], industry || [], jobTargets || []);

  if (!isEmpty(audiences)) {
    dimensions.push({
      '@odata.type': '#Model.AudienceDimension',
      Type: 'Audience',
      Audiences: audiences,
    });
  }

  if (!isEmpty(AgeRanges)) {
    dimensions.push({
      '@odata.type': '#Model.AgeDimension',
      Type: 'Age',
      AgeRanges,
    });
  }

  if (!isEmpty(GenderTypes)) {
    dimensions.push({
      '@odata.type': '#Model.GenderDimension',
      Type: 'Gender',
      GenderTypes,
    });
  }

  if (!isEmpty(profiles)) {
    dimensions.push({
      '@odata.type': '#Model.ProfileDimension',
      Type: 'Profile',
      Profiles: profiles,
    });
  }

  const audienceGroupModel = {
    ...data,
    Dimensions: dimensions,
  };

  return audienceGroupModel;
};

export const viewModelToBulkModelForAudienceGroup = (audienceGroupViewModel) => {
  const model = pick(audienceGroupViewModel.data, whitelistedBulkModelSchemaPropertyKeys);

  const {
    CustomerData,
    Interests,
    companyNameTargets = {},
  } = audienceGroupViewModel.data;

  model.Audiences = union(CustomerData, Interests);

  if (!isEmpty(companyNameTargets)) {
    model.companyNameTargets = pluck(companyNameTargets, 'Target');
  }

  if (!model.Id) {
    model.Id = AudienceGroupNegativeReferenceId; // For creation
  }

  return model;
};

export const bulkModelToViewModelForAudienceGroup = (audienceGroupModel) => {
  const Audiences = audienceGroupModel.Audiences || [];

  const audienceGroupViewModel = {
    data: {
      ...pick(audienceGroupModel, whitelistedBulkModelSchemaPropertyKeys),
      CustomerData: Audiences.filter(audience => audience.Type !== 'InMarket'),
      Interests: Audiences.filter(audience => audience.Type === 'InMarket'),
    },
    isValid: true,
    isDirty: false,
  };
  return audienceGroupViewModel;
};

export const fetchAudienceGroups = ({
  cid,
  aid,
  keyword,
  top,
  skip,
}) => edmCampaign
  .Customers.$withKey(cid)
  .Accounts.$withKey(aid)
  .AudienceGroups
  .get({
    count: true,
    filter: keyword ? { where: `contains(tolower(Name),'${keyword.toLowerCase()}')` } : {},
    top,
    skip,
  })
  .toPromise();

export const fetchAudienceGroup = ({
  cid,
  aid,
  audienceGroupId,
}) => edmCampaign
  .Customers.$withKey(cid)
  .Accounts.$withKey(aid)
  .AudienceGroups.$withKey(audienceGroupId)
  .get()
  .toPromise();

export const createOrUpdateAudienceGroup = (data) => {
  const createOrUpdateCall = data && data.Id > 0 ?
    audienceGroupResource.bulkUpdate.bind(audienceGroupResource) :
    audienceGroupResource.bulkCreate.bind(audienceGroupResource);

  return createOrUpdateCall(data);
};

export const saveAudienceGroup = (audienceGroupViewModel, i18n) => {
  const audienceGroupModel = viewModelToBulkModelForAudienceGroup(audienceGroupViewModel);

  return createOrUpdateAudienceGroup(audienceGroupModel)
    // with bulk the result will have shape of:
    // - { ...entityData } - in case of success;
    // - { entity: { ...entityData }, errors: [(EditorialError|MTError)] } - in case of error(s).
    .then(
      bulkResult => bulkModelToViewModelForAudienceGroup(bulkResult),
      (bulkOrSystemError) => {
        if (isBulkWrappedResponse(bulkOrSystemError)) {
          const { entity, errors } = bulkOrSystemError;

          // eslint-disable-next-line prefer-promise-reject-errors
          return Promise.reject({
            audienceGroup: entity ? bulkModelToViewModelForAudienceGroup(entity) : audienceGroupViewModel,
            errors: pluck(getBulkApiErrors(errors, 'AudienceGroup', i18n), 'message'), // , activityLogger),
          });
        }

        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise.reject({
          audienceGroup: audienceGroupViewModel,
          errors: [i18n.getString(GENERIC_ERROR_MESSAGE_KEY)],
        });
      }
    );
};

const updateAudienceGroups = audienceGroupResource.bulkUpdateMany.bind(audienceGroupResource);

export const deleteAudienceGroups = (audienceGroups, i18n) => {
  const idList = map(audienceGroups, audienceGroup => audienceGroup.Id);
  const attrList = map(audienceGroups, () => ({ Status: 'Deleted' }));
  return updateAudienceGroups(idList, attrList)
    .then((bulkManyResult) => {
      const errors = chain(bulkManyResult)
        .map(response => result(response, 'errors'))
        .compact()
        .map(error => pluck(getBulkApiErrors(error, 'AudienceGroup'), 'message'))
        .flatten()
        .uniq(true)
        .value();
      return Promise.resolve({ errors });
    }).catch(Promise.resolve({ errors: [i18n.getString(GENERIC_ERROR_MESSAGE_KEY)] }));
};
